/**
 * NEW_PLANS_FLAG
 *
 * Centralized feature flag for the new plans UI (PlansCards, UpgradeFeatureModal,
 * UpgradeWindow, UpgradePlanSuccessModal, new account-creation plan/payment pages).
 *
 * Gate: both `beta_tester` AND `beta_tester_internal` user flags must be true.
 *
 * To launch to all users:
 *   1. Search for "isNewPlans" / "isNewPlansEnabled" across the codebase
 *   2. Remove all conditional branches, keeping only the "new plans" code
 *   3. Delete this file
 *   4. Remove legacy account-creation pages (src/views/Auth/AccountCreation/legacy/)
 */

import { computed } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { tagsFlagEnabled } from '@/helpers/posthog'

// The environment predicates moved to `helpers/env.js` — `UserStore` needs them
// for the pilot gate and this file imports that store, so they had to live
// somewhere that imports nothing. Re-exported here because every existing call
// site reads them from this module.
// Imported AND re-exported: a bare `export … from` re-export does not bind the
// names in this module's own scope, and `isLocalhost()` is called below.
import { isLocalhost, isStaging, isNonProdEnv } from '@/helpers/env'

export { isLocalhost, isStaging, isNonProdEnv }

export const useNewPlans = () => {
  const userStore = useUserStore()
  return computed(() => !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal))
}

export const isNewPlansEnabled = () => {
  const userStore = useUserStore()
  return !!(userStore.flags?.beta_tester && userStore.flags?.beta_tester_internal)
}

/**
 * Inventory redesign — the whole rollout, beta cohort only.
 *
 * Gate: the PostHog `inventory-beta` experiment (`test` in, `control` out) OR
 * the email allowlist in `constants/pilots.js`, so the named pilots stay in
 * however PostHog assigns them. Localhost always on. Add new testers in
 * PostHog; no deploy. See `UserStore.isInventoryPilot` for the exact order.
 *
 * The predicate itself lives on `UserStore.isInventoryPilot` because
 * `isNewDesign` is AND-ed with it, and this file imports the store (defining it
 * here would be a circular import). This composable is the ergonomic wrapper
 * for components.
 *
 * Gates every part of the feature:
 *   - the design itself, via `isNewDesign` → every grid / cell / panel already
 *     branching on it, no per-consumer flag check needed;
 *   - the opt-in surfaces that advertise it — the header switch + its confirm
 *     modal, the sidebar promo, the welcome modals, the first-run tour and the
 *     user-menu row. Those are checked explicitly so a classic user never even
 *     sees the invitation;
 *   - inventory results in global search, via `isSearchInventoryEnabled()`
 *     below, which is this same getter (it also switches the `/search`
 *     backend).
 */
export const useInventoryPilot = () => {
  const userStore = useUserStore()
  return computed(() => userStore.isInventoryPilot)
}

/**
 * Inventory hits inside GLOBAL SEARCH (CDP-2508).
 *
 * The `/search` response can carry an optional `inventory` block (the user's own
 * matching cards), which turns a Card Database row's "Add Card" into
 * "View in Inventory" / "View in Batch". Everyone outside the cohort keeps the
 * previous behaviour exactly — the store drops the block, so there is nothing
 * for the UI to render and no branch to hit.
 *
 * Deliberately the SAME predicate as the redesign — the PostHog `inventory-beta`
 * test variant OR the named email allowlist, via `UserStore.isInventoryPilot`.
 * Searching your own inventory is part of the beta, so one assignment in
 * PostHog turns on both halves and there's nothing to keep in sync.
 *
 * It was briefly its own copy of "PostHog OR allowlist", minus that getter's
 * fallback to the user's persisted `new_design` opt-in while flags are
 * unresolved. That fallback isn't only about the column-layout race it was
 * written for: "unresolved" is also the permanent state when an AD BLOCKER
 * blocks PostHog, and a beta user in that position would have kept the new
 * design while silently losing inventory hits — and having their `/search` go
 * to the batch processor. Half a beta is worse than either half.
 *
 * ⚠️ This flag ALSO picks the API host: cohort users hit the main API for
 * `/search` (`apiClient()`), everyone else the batch processor
 * (`apiClient(true)`) — see `SearchStore.searchDatabaseMatches`. So a user in
 * the experiment gets a different backend, not just extra UI.
 */
export const isSearchInventoryEnabled = () => useUserStore().isInventoryPilot

export const useSearchInventory = () => computed(() => isSearchInventoryEnabled())

/**
 * The All Batches page inside Inventory (`/inventory/batches`).
 *
 * TWO conditions, AND-ed:
 *   1. the new design — the page is built from new-design chrome (the Inventory
 *      breadcrumb, `AppDataTable`, the new sidebar's rail width) and would read
 *      as broken inside the legacy shell;
 *   2. the Inventory beta cohort itself (`isInventoryPilot`), which is the
 *      allowlist OR the PostHog arm.
 *
 * It ran on a two-name trial list of its own first; now it ships to everyone in
 * the beta, so the page and the design it is built out of can't drift apart.
 * Still an AND, not the OR `INVENTORY_PILOT_EMAILS` uses for the beta itself:
 * being in the cohort is permission to see the page, not a way around the
 * redesign gate.
 *
 * Gates BOTH the route (`handleInventoryBatches`) and every entry point, via
 * `batchesListPath()`. Guarding only the route would leave a "All Batches" link
 * in the sidebar that bounced people straight back to the legacy page.
 */
export const isAllBatchesPageEnabled = () => {
  if (isLocalhost()) return true

  const userStore = useUserStore()

  // Same cohort as the rest of the Inventory beta — `isInventoryPilot` is the
  // one place that cohort is decided (allowlist OR the PostHog arm), so this
  // page can't drift from the design it is built out of. It had its own
  // two-name list while it was in trial; that trial is over.
  return userStore.isNewDesign && userStore.isInventoryPilot
}

export const useAllBatchesPage = () => computed(() => isAllBatchesPageEnabled())

/**
 * Product Tags — every surface of the feature, behind the PostHog `tags` flag.
 *
 * Gate: `posthog.isFeatureEnabled('tags')`, read once in `helpers/posthog.js`
 * and kept in a ref so this re-evaluates when flags resolve. Unresolved reads
 * as OFF (a new feature has nothing to fall back on). Localhost and staging
 * (`v2-dev`) are always on, so QA sees the feature without being targeted — and
 * an ad blocker eating PostHog there can't hide it. Production honours the flag.
 * Targeting is managed in PostHog; no deploy to add a dealer.
 *
 * Gated at the choke points rather than in every tag component:
 *   - the TAGS COLUMN — dropped from the column defs in `configuredGridOptions`
 *     (useAgGrid.js), which both card grids build through, so it isn't merely
 *     hidden: a saved layout that says "tags visible" has no column to show.
 *     The Columns picker (`ColumnCheckboxList`) and its "Columns shown N" count
 *     (`countVisibleColumns`) leave the row out too;
 *   - the card panels — Inspector (desktop + mobile), Inventory details, and
 *     the tags field in Create Batch, whose `tag_ids` is then not sent;
 *   - the bulk bars' Tags button and the Tags-column bulk editor;
 *   - the Tags FILTER (`getCardGridFilters` / `getAvailableFilters`);
 *   - Settings → Tags: the nav row and the route.
 * Not mounting those is also what gates the REQUESTS: `GET /tags` is only ever
 * made by a tag component mounting (`CardTags`, `TagPicker`, the settings page).
 */
export const isTagsEnabled = () => isNonProdEnv() || tagsFlagEnabled.value

export const useTags = () => computed(() => isTagsEnabled())

/*
 * Launched, flags removed (2026-10-02):
 *   import batches — the source labels ("From CollX Import") and the batch
 *                   tools an import doesn't get (no reprocess, no banner, no
 *                   adding cards, read-only type / sport / locked sets). On for
 *                   everyone; the email allowlist (`IMPORT_BATCHES_EMAILS`) is
 *                   gone. Still keyed on the backend's `is_import`, read at the
 *                   same two choke points: `BatchStore.isImportBatch` and
 *                   `importSourceLabel` (helpers/batch.js).
 *
 * Launched, flags removed (2026-09-24):
 *   round up prices — the sub-$1 lift on the eBay AND Whatnot listing templates
 *                   (`round_up_sale_price_to_dollar`): the toggle on the eBay
 *                   listing-template screen, the same toggle in the shared
 *                   settings dialog, the On/Off readout in
 *                   `TemplateSettingsOverview`, and `roundsUpSalePrice` in
 *                   `helpers/batch.js`, which forgives that marketplace's floor
 *                   error on Publish. On for everyone. The floor error itself
 *                   was never gated; now that every dealer has the toggle, its
 *                   message always offers rounding up as the second way out.
 *
 * Launched, flags removed (2026-09-15):
 *   title character count — the "45/80" counter and its over-limit red state on
 *                   the Custom Title, in the grid editor, the Inspector,
 *                   Inventory details and the eBay listing title. On for
 *                   everyone. It still shows only where the 80-character cap
 *                   actually applies (`ebayEnabled`), which is a fact about
 *                   eBay, not a gate.
 *
 * Launched, flags removed (2026-09-02):
 *   title abbreviations — the "Use abbreviations for card attributes and teams"
 *                   checkbox in the title-template modal. On for everyone; the
 *                   setting itself is `title_team_abbreviations_enabled` on the
 *                   user and is unchanged.
 *   shopify location — the default inventory location section + its
 *                   `/users/me/shopify/locations` fetch. On for everyone; the
 *                   section still hides itself when the store has no locations
 *                   to choose between.
 *
 * Launched, flags removed (2026-08-24):
 *   `bulk-delist` — Bulk End / Relist. Now on for everyone.
 *   `csv-import`  — Inventory CSV import. Now on for everyone; the PLAN
 *                   entitlement (`csv-inventory-import`) still gates it inside
 *                   `InventoryCsvImportModal` and is unrelated to this file.
 */
